import type { ElementType } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { BasicParagraph, SmallParagraph, style } from '@genially/design-system';
import DisplayDownIcon from '@genially/icons/lib/display-down/DisplayDownIcon';
import DisplayUpIcon from '@genially/icons/lib/display-up/DisplayUpIcon';

export const DataText = styled(SmallParagraph)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

export const OrderNumber = styled.div(({ theme: { size } }) => ({
  width: size.units(3.25),
  height: size.units(3.25),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

interface UserAliasProps {
  $hasSpaces?: boolean;
  $centered?: boolean;
}

export const UserAlias = styled(BasicParagraph)<UserAliasProps>(
  ({ theme: { color, size }, $hasSpaces, $centered }) => ({
    color: color.content.reversed.default(),
    textAlign: $centered ? 'center' : 'left',
    paddingRight: size.units(1.5),

    ...style.mixins.ellipsis(true),

    whiteSpace: $hasSpaces ? 'normal' : 'break-spaces',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflowWrap: 'anywhere',
  }),
);

const fadeIn = keyframes({
  '0%': {
    opacity: 0,
  },
  '100%': {
    opacity: 1,
  },
});

const withAnimation = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean; $delayAnimation?: number }>(
    ({ $activeAnimation, $delayAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${fadeIn} 0.75s cubic-bezier(0.25, 0.46, 0.45, 0.94)
          ${$delayAnimation}s both;
      `,
  );

interface WrapperProps {
  $isSelected?: boolean;
  $zIndex?: number;
}

export const Wrapper = withAnimation(
  styled.div<WrapperProps>(({ theme: { color, size }, $isSelected, $zIndex }) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: size.units(1),
    height: size.units(6),
    overflow: 'hidden',
    backgroundColor: $isSelected
      ? color.background.secondary.default(0.1)
      : color.background.tertiary.default(0.05),
    padding: size.units(1),
    paddingRight: size.units(2),
    borderRadius: size.units(1),
    border: $isSelected ? `1px solid ${color.border.reversed.default()}` : undefined,
    zIndex: $zIndex,

    [OrderNumber]: {
      flexShrink: 0,
    },

    [UserAlias]: {
      flexGrow: 1,
    },
  })),
);

interface ShowProps {
  $show: boolean;
}

export const StyledDisplayUpIcon = styled(DisplayUpIcon)<ShowProps>(
  ({ theme: { color, size, transition }, $show }) => ({
    color: color.content.success.default(),
    width: size.units(2),
    height: size.units(2),
    opacity: $show ? 1 : 0,
    transition: transition.standard('opacity'),
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-100%, -50%)',
  }),
);

export const StyledDisplayDownIcon = styled(DisplayDownIcon)<ShowProps>(
  ({ theme: { color, size, transition }, $show }) => ({
    color: color.content.error.default(),
    width: size.units(2),
    height: size.units(2),
    opacity: $show ? 1 : 0,
    transition: transition.standard('opacity'),
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-100%, -50%)',
  }),
);

export const ChangePositionIconWrapper = styled.div(() => ({
  position: 'relative' as const,
  height: '100%',
}));
